/**
 * BackgroundJobsIndicator — header icon + popover listing LLM-backed
 * Temporal workflows. Modeled on UserMenu's button+dropdown pattern; no
 * new primitive. Load-more pattern cloned from CompletionLog.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  closePopover,
  loadMoreRequested,
  selectHasMore,
  selectLoading,
  selectLoadingMore,
  selectOrderedBackgroundTasks,
  selectPopoverOpen,
  selectRunningTaskCount,
  togglePopover,
} from '../ducks';
import { BackgroundJobRow } from './BackgroundJobRow';

export function BackgroundJobsIndicator() {
  const dispatch = useDispatch();
  const open = useSelector(selectPopoverOpen);
  const tasks = useSelector(selectOrderedBackgroundTasks);
  const runningCount = useSelector(selectRunningTaskCount);
  const hasMore = useSelector(selectHasMore);
  const loading = useSelector(selectLoading);
  const loadingMore = useSelector(selectLoadingMore);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target as Node)) return;
      dispatch(closePopover());
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, dispatch]);

  const handleToggle = useCallback(() => {
    dispatch(togglePopover());
  }, [dispatch]);

  const handleLoadMore = useCallback(() => {
    dispatch(loadMoreRequested());
  }, [dispatch]);

  return (
    <div className="bg-jobs" data-testid="bg-jobs-indicator" ref={rootRef}>
      <button
        className={`bg-jobs__trigger${runningCount > 0 ? ' bg-jobs__trigger--active' : ''}`}
        onClick={handleToggle}
        title="Background jobs"
        aria-label="Background jobs"
        data-testid="bg-jobs-trigger"
      >
        <span className="bg-jobs__icon" aria-hidden="true">⚙</span>
        {runningCount > 0 && (
          <span className="bg-jobs__badge" data-testid="bg-jobs-badge">{runningCount}</span>
        )}
      </button>
      {open && (
        <div className="bg-jobs__dropdown" data-testid="bg-jobs-dropdown">
          <div className="bg-jobs__header">
            <span className="bg-jobs__title">Background jobs</span>
          </div>
          <div className="bg-jobs__list">
            {tasks.length === 0 && !loading && (
              <div className="bg-jobs__empty">No background jobs yet.</div>
            )}
            {tasks.map((t) => (
              <BackgroundJobRow key={t.id} task={t} />
            ))}
            {loading && tasks.length === 0 && (
              <div className="bg-jobs__loading">Loading…</div>
            )}
          </div>
          {hasMore && (
            <button
              className="bg-jobs__load-more"
              onClick={handleLoadMore}
              disabled={loadingMore}
              data-testid="bg-jobs-load-more"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default BackgroundJobsIndicator;
